import type { CSSProperties, ReactNode } from 'react';
import type { LocalCard } from '../lib/db.ts';

/** Colour a card that has none, deterministically, so it looks the same on every device. */
export function autoColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 52% 40%)`;
}

export const cardColor = (card: Pick<LocalCard, 'id' | 'color'>): string => card.color ?? autoColor(card.id);

/**
 * Ink for text on a card. Most card colours are deep enough for white, but a mustard or a pale
 * pastel is not — so measure, rather than hope (WCAG relative luminance of a #rrggbb colour).
 */
export function inkOn(color: string): string {
  const hex = /^#([0-9a-f]{6})$/i.exec(color)?.[1];
  if (!hex) return '#fff';
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  return luminance > 0.36 ? '#1b1a17' : '#fff';
}

/** The first letter that is a letter or digit, so "  #Coop" still shows C. */
export const monogram = (name: string): string => name.match(/[\p{L}\p{N}]/u)?.[0]?.toUpperCase() ?? '•';

/** Shared name for the View Transition that grows a tile into the till screen. */
export const cardTransitionName = (id: string) => `card-${id.replace(/[^A-Za-z0-9_-]/g, '')}`;

/**
 * A loyalty card as the object it is: ISO card proportions, the brand's colour, the initial
 * printed large and cropped off the edge the way plastic cards carry their logo.
 */
export function CardTile({
  card, onClick, badge, size = 'm', transition = true,
}: {
  card: Pick<LocalCard, 'id' | 'name' | 'color' | 'favorite'>;
  onClick?: () => void;
  /** A short line under the name: "Qui vicino", "Scade domani". */
  badge?: ReactNode;
  size?: 's' | 'm';
  transition?: boolean;
}) {
  const color = cardColor(card);
  const style = {
    '--c': color,
    '--on-c': inkOn(color),
    ...(transition ? { viewTransitionName: cardTransitionName(card.id) } : {}),
  } as CSSProperties;

  const body = (
    <>
      <span className="monogram" aria-hidden="true">{monogram(card.name)}</span>
      {card.favorite && <span className="fav" aria-label="Preferita">★</span>}
      <span className="name">{card.name}</span>
      {badge && <span className="badge">{badge}</span>}
    </>
  );

  return onClick ? (
    <button type="button" className={`wallet-card ${size}`} style={style} onClick={onClick}>{body}</button>
  ) : (
    <div className={`wallet-card ${size}`} style={style}>{body}</div>
  );
}
